import type {
  CombatActionResponseDTO,
  CombatLogEntryDTO,
  CombatPosition,
  CombatStateDTO,
} from '@hexcrawl/shared-utils';
import { EntityStatus, EntityType } from '@hexcrawl/shared-utils';

export type AbilityStartSkip = {
  abilityId: string;
  casterId: string;
};

export type TurnEvent =
  | { type: 'actor_move'; entityId: string; to: CombatPosition }
  | { type: 'ability_start'; entry: CombatLogEntryDTO }
  | { type: 'apply_delta'; entityId: string; hpChange?: number; mpChange?: number }
  | { type: 'batch_apply_delta'; targets: Array<{ entityId: string; hpChange?: number; mpChange?: number }> }
  | { type: 'ability_complete'; entry: CombatLogEntryDTO }
  | { type: 'death_sequence'; entityId: string }
  | { type: 'add_log_entry'; entry: CombatLogEntryDTO }
  | { type: 'combat_end'; result: 'victory' | 'defeat' }
  | { type: 'finalize'; response: CombatActionResponseDTO };

export type EnqueueOptions = {
  skipAbilityStart?: AbilityStartSkip;
  /** Snapshot MP for the player character before this turn's changes (character.currentMP). */
  playerCurrentMP?: number;
  /** Snapshot HP for the player character before this turn's changes (character.currentHP). */
  playerCurrentHP?: number;
};

/**
 * Build an ordered sequence of TurnEvents from a CombatActionResponseDTO.
 *
 * The event list drives the CombatScene's sequential playback so that
 * movement, ability animations, HP/MP updates, and death sequences
 * play one at a time without overlapping.
 */
export function buildTurnEvents(
  response: CombatActionResponseDTO,
  _snapshot: CombatStateDTO,
  options: EnqueueOptions = {}
): TurnEvent[] {
  const events: TurnEvent[] = [];
  const { skipAbilityStart } = options;

  // Index position and status deltas by entityId for quick lookup
  const positionDeltas = new Map<string, CombatPosition>();
  const statusDeltas = new Map<string, EntityStatus>();
  for (const delta of response.entityStateDeltas) {
    if (delta.position) positionDeltas.set(delta.entityId, delta.position);
    if (delta.status !== undefined) statusDeltas.set(delta.entityId, delta.status);
  }

  // Pre-compute HP/MP changes per entity (snapshot vitals → final vitals).
  // EntityCombatStateDTO carries no vitals; they come from enemyStates for enemies and
  // from the caller-supplied player values for the player character. An entity with no
  // snapshot (a hireling NPC) is skipped rather than diffed against a guessed zero.
  const snapshotMPById = new Map<string, number>();
  const snapshotHPById = new Map<string, number>();
  for (const enemyState of _snapshot.enemyStates) {
    snapshotMPById.set(enemyState.entityId, enemyState.currentMP);
    snapshotHPById.set(enemyState.entityId, enemyState.currentHP);
  }
  const playerEntityState = _snapshot.entityStates.find(e => e.entityType === EntityType.PLAYER);
  if (playerEntityState) {
    if (options.playerCurrentMP !== undefined) {
      snapshotMPById.set(playerEntityState.entityId, options.playerCurrentMP);
    }
    if (options.playerCurrentHP !== undefined) {
      snapshotHPById.set(playerEntityState.entityId, options.playerCurrentHP);
    }
  }
  const mpChanges = new Map<string, number>();
  const hpChanges = new Map<string, number>();
  for (const delta of response.entityStateDeltas) {
    if (delta.currentMP !== undefined && snapshotMPById.has(delta.entityId)) {
      const change = delta.currentMP - snapshotMPById.get(delta.entityId)!;
      if (change !== 0) mpChanges.set(delta.entityId, change);
    }
    if (delta.currentHP !== undefined && snapshotHPById.has(delta.entityId)) {
      const change = delta.currentHP - snapshotHPById.get(delta.entityId)!;
      if (change !== 0) hpChanges.set(delta.entityId, change);
    }
  }

  const consumedMovement = new Set<string>();
  const queuedDeaths = new Set<string>();
  const consumedMPDrain = new Set<string>();
  const consumedHPChange = new Set<string>();

  for (const action of response.actions) {
    const actorId = action.entityId;

    // 1. Actor movement (if position changed and not yet consumed)
    if (positionDeltas.has(actorId) && !consumedMovement.has(actorId)) {
      consumedMovement.add(actorId);
      events.push({ type: 'actor_move', entityId: actorId, to: positionDeltas.get(actorId)! });
    }

    // 2. Log entry (appears in log panel in sync with the action)
    events.push({ type: 'add_log_entry', entry: action });

    // 3. Caster MP drain — fire before ability_start so the mana bar animates as the cast begins.
    //    Uses apply_delta with mpChange only (no hpChange), which skips the queue stall delay.
    if (action.abilityUsed && !consumedMPDrain.has(actorId)) {
      const mpChange = mpChanges.get(actorId);
      if (mpChange !== undefined) {
        consumedMPDrain.add(actorId);
        events.push({ type: 'apply_delta', entityId: actorId, mpChange });
      }
    }

    // 4. Ability start animation (once per cast)
    if (action.abilityUsed) {
      const shouldSkip =
        skipAbilityStart &&
        action.entityId === skipAbilityStart.casterId &&
        action.abilityUsed.abilityId === skipAbilityStart.abilityId;

      if (!shouldSkip) {
        events.push({ type: 'ability_start', entry: action });
      }
    }

    // 5. Ability complete animation (explosion, hit reactions — fires before HP bars)
    if (action.abilityUsed) {
      events.push({ type: 'ability_complete', entry: action });
    }

    // 6. Batch apply HP changes from all hit targets (after the impact visuals)
    if (action.abilityUsed?.targets.length) {
      const deltaTargets = action.abilityUsed.targets
        .filter(t => t.damage > 0)
        .map(t => ({ entityId: t.targetId, hpChange: -t.damage }));

      if (deltaTargets.length > 0) {
        for (const target of deltaTargets) consumedHPChange.add(target.entityId);
        events.push({ type: 'batch_apply_delta', targets: deltaTargets });
      }
    }

    // 7. Death sequence for any targets that died
    if (action.abilityUsed?.targets) {
      for (const hit of action.abilityUsed.targets) {
        const targetId = hit.targetId;
        if (statusDeltas.get(targetId) === EntityStatus.DEAD && !queuedDeaths.has(targetId)) {
          queuedDeaths.add(targetId);
          events.push({ type: 'death_sequence', entityId: targetId });
        }
      }
    }
  }

  // Handle any vitals change no ability accounted for — a potion drunk in combat is
  // reported only as a delta, so without this its bar would jump silently (or not at all).
  for (const [entityId, hpChange] of hpChanges) {
    if (consumedHPChange.has(entityId)) continue;
    const mpChange = consumedMPDrain.has(entityId) ? undefined : mpChanges.get(entityId);
    if (mpChange !== undefined) consumedMPDrain.add(entityId);
    events.push({ type: 'apply_delta', entityId, hpChange, mpChange });
  }
  for (const [entityId, mpChange] of mpChanges) {
    if (consumedMPDrain.has(entityId)) continue;
    events.push({ type: 'apply_delta', entityId, mpChange });
  }

  // Handle any unconsumed position deltas (entities that moved without an action entry)
  for (const [entityId, pos] of positionDeltas) {
    if (!consumedMovement.has(entityId)) {
      events.push({ type: 'actor_move', entityId, to: pos });
    }
  }

  // Handle any unconsumed death statuses
  for (const [entityId, status] of statusDeltas) {
    if (status === EntityStatus.DEAD && !queuedDeaths.has(entityId)) {
      events.push({ type: 'death_sequence', entityId });
    }
  }

  // Combat end event if victory or defeat
  if (response.victory) {
    events.push({ type: 'combat_end', result: 'victory' });
  } else if (response.defeat) {
    events.push({ type: 'combat_end', result: 'defeat' });
  }

  // Finalize: apply combatDelta and reconcile all state
  events.push({ type: 'finalize', response });

  return events;
}
